import React, { useEffect, useMemo, useState } from 'react';
import { Meeting, MeetingAttendee } from '../../../../types/meeting';
import { AuthUser } from '../../../../types/auth';
import meetingsAPI from '../../../../services/meetingsApi';

export interface AddAttendeeInput {
  name: string;
  role?: string;
  // When set, links the attendee to a real user account (grants them meeting
  // visibility). Name/role are derived from the user, so the free-text fields
  // are hidden in this mode.
  userId?: string;
}

interface MeetingAttendeesPanelProps {
  meeting: Meeting;
  // Secretary/Admin: may add and remove attendees. Others see a read-only roster.
  canManage?: boolean;
  onAddAttendee: (attendee: AddAttendeeInput) => Promise<void>;
  onRemoveAttendee: (attendee: MeetingAttendee) => void;
}

const initials = (name: string): string =>
  name.split(' ').map((p) => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

const MeetingAttendeesPanel: React.FC<MeetingAttendeesPanelProps> = ({ meeting, canManage = false, onAddAttendee, onRemoveAttendee }) => {
  const attendees = useMemo(() => meeting.attendees || [], [meeting.attendees]);
  const [collapsed, setCollapsed] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [newName, setNewName] = useState('');
  const [newRole, setNewRole] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [users, setUsers] = useState<AuthUser[]>([]);

  // Load selectable users once the add form opens. Secretary-only server-side;
  // returns [] for anyone else, so the form silently falls back to manual entry.
  useEffect(() => {
    if (!showAddForm || users.length > 0) return;
    let cancelled = false;
    meetingsAPI.listUsers().then((u) => {
      if (!cancelled) setUsers(u);
    });
    return () => {
      cancelled = true;
    };
  }, [showAddForm, users.length]);

  // Users not already linked to an attendee on this meeting.
  const linkedUserIds = useMemo(
    () => new Set(attendees.map((a) => a.user_id).filter(Boolean) as string[]),
    [attendees]
  );
  const availableUsers = useMemo(
    () => users.filter((u) => !linkedUserIds.has(u.id)),
    [users, linkedUserIds]
  );

  const resetAddForm = () => {
    setShowAddForm(false);
    setSelectedUserId('');
    setNewName('');
    setNewRole('');
  };

  const linkingUser = selectedUserId !== '';

  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isAdding) return;

    if (linkingUser) {
      const user = users.find((u) => u.id === selectedUserId);
      if (!user) return;
      setIsAdding(true);
      try {
        // Prefer the descriptive title (e.g. "Chief Financial Officer") for the
        // attendee role; fall back to the access-level role when unset.
        await onAddAttendee({
          name: user.display_name,
          role: user.title?.trim() || user.role,
          userId: user.id,
        });
        resetAddForm();
      } catch {
        // Error is surfaced by the parent; keep the selection so the user can retry.
      } finally {
        setIsAdding(false);
      }
      return;
    }

    const name = newName.trim();
    if (!name) return;
    setIsAdding(true);
    try {
      await onAddAttendee({ name, role: newRole.trim() || undefined });
      resetAddForm();
    } catch {
      // Error is surfaced by the parent; keep the form values so the user can retry.
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <div className="meeting-panel">
      <div
        className="meeting-panel-header meeting-panel-header-collapsible"
        role="button"
        aria-expanded={!collapsed}
        onClick={() => setCollapsed((c) => !c)}
      >
        <div>
          <h3 className="meeting-panel-title">Attendees</h3>
          {attendees.length > 0 && (
            <p className="meeting-panel-subtitle">{attendees.length} participants</p>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {canManage && (
            <button
              className="meeting-btn meeting-btn-ghost meeting-btn-sm"
              onClick={(e) => {
                e.stopPropagation();
                setCollapsed(false);
                setShowAddForm(true);
              }}
            >
              Add Attendee
            </button>
          )}
          <span className={`meeting-panel-chevron ${collapsed ? '' : 'expanded'}`}>▾</span>
        </div>
      </div>

      {!collapsed && canManage && showAddForm && (
        <form className="attendee-add-form" onSubmit={handleAddSubmit}>
          {availableUsers.length > 0 && (
            <select
              className="meeting-form-input"
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              disabled={isAdding}
              aria-label="Link an existing user"
            >
              <option value="">Add someone not listed…</option>
              {availableUsers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.display_name}{u.title ? ` — ${u.title}` : ` (${u.role})`}
                </option>
              ))}
            </select>
          )}
          {!linkingUser && (
            <>
              <input
                className="meeting-form-input"
                placeholder="Name *"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                disabled={isAdding}
                autoFocus
              />
              <input
                className="meeting-form-input"
                placeholder="Role (optional)"
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
                disabled={isAdding}
              />
            </>
          )}
          <div className="attendee-add-form-actions">
            <button
              type="button"
              className="meeting-btn meeting-btn-ghost meeting-btn-sm"
              onClick={resetAddForm}
              disabled={isAdding}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="meeting-btn meeting-btn-primary meeting-btn-sm"
              disabled={(linkingUser ? false : !newName.trim()) || isAdding}
            >
              {isAdding ? 'Adding...' : 'Add'}
            </button>
          </div>
        </form>
      )}

      {!collapsed && (attendees.length === 0 ? (
        !showAddForm && (
          <div className="meeting-empty-hint">
            {canManage
              ? 'No attendees yet. Add the meeting participants to get started.'
              : 'No attendees have been added yet.'}
          </div>
        )
      ) : (
        <div className="attendees-grid">
          {attendees.map((a) => (
            <div className="attendee-card" key={a.attendee_id}>
              <div className="attendee-avatar">{initials(a.name)}</div>
              <div className="attendee-info">
                <p className="attendee-name">{a.name}</p>
                <p className="attendee-role">
                  {[a.role, a.organization].filter(Boolean).join(' · ')}
                </p>
                {a.stance && <p className="attendee-stance">"{a.stance}"</p>}
              </div>
              {canManage && (
                <button
                  className="attendee-remove-btn"
                  title={`Remove ${a.name}`}
                  aria-label={`Remove ${a.name}`}
                  onClick={() => onRemoveAttendee(a)}
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

export default MeetingAttendeesPanel;
